import React, {useEffect, useMemo, useRef, useState} from 'react';
import {View, Text, ScrollView, TouchableOpacity, StyleSheet} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';
import {
  redirectToProductDetail,
  shuffleToItemBySku,
  openBoxChampOptions
} from '../../actions/scanpackScanningActions';
import {GetBoxChampSortPreference, SaveBoxChampSortPreference} from '../../actions/userAction';
import BoxChampZonedList from '../scanpack/BoxChampZonedList';
import {sortUnscannedItems} from '../scanpack/boxChampSort';
import {useBoxChampGuided} from './hooks/useBoxChampGuided';
import {isBoxChampEnabled} from './utils/boxChampGuidedSelectors';
import OrderItem from '../../commons/orderItem';
import OrderItemImage from '../../commons/orderItemImage';
import OrderKitItem from '../../commons/orderKitItem';
import { getTotalUnScannedCount } from '../scanToCartScanning/utils/scanningUtils';
import { isFlatDisplayItem } from './utils/scanningUtils';
import { getUnscannedUnitCount } from './utils/itemQuantities';
import useWidth from '../scanToCartScanning/useWidth';
import ScrollableItemList from './ScrollableItemList';
import NotFulfilledItems from '../scanpack/NotFulfilledItems';
import {checkIfAllFromNotFulfilled} from '../scanpack/scanBarcodeFunctions';
import {getNotFulfilledItems} from './utils/boxChampGuidedSelectors';

const UnScannedItemsList = () => {
  const dispatch = useDispatch();
  const windowWidth = useWidth();

  const items = useSelector(state => state.scanPackScanning.currentOrder?.unscanned_items);
  const boxes = useSelector(state => state.scanPackScanning.currentOrder?.boxes);
  const orderItemBoxes = useSelector(state => state.scanPackScanning.currentOrder?.order_item_boxes);
  const boxChampEnabled = useSelector(isBoxChampEnabled);
  const notFulfilledItems = useSelector(getNotFulfilledItems);
  const {view, paused} = useBoxChampGuided();

  const sortAvailable = !view && boxChampEnabled && (orderItemBoxes?.length || 0) > 0;
  const [sortMode, setSortMode] = useState('location');
  const sortChosenRef = useRef(false);
  useEffect(() => {
    if (!sortAvailable) return undefined;
    let live = true;
    Promise.resolve(dispatch(GetBoxChampSortPreference())).then(mode => {
      if (live && mode && !sortChosenRef.current) setSortMode(mode);
    });
    return () => {
      live = false;
    };
  }, [sortAvailable]);
  const toggleSort = () => {
    const mode = sortMode === 'packing' ? 'location' : 'packing';
    sortChosenRef.current = true;
    setSortMode(mode);
    dispatch(SaveBoxChampSortPreference(mode));
  };
  const displayItems = useMemo(
    () => (sortAvailable ? sortUnscannedItems(items || [], sortMode, boxes, orderItemBoxes) : items || []),
    [sortAvailable, items, sortMode, boxes, orderItemBoxes]
  );

  const onItemPress = (item) => {
    dispatch(redirectToProductDetail(item.product_id));
  };

  const onShuffle = (sku, childSku) => dispatch(shuffleToItemBySku(sku, childSku));

  const totalUnScannedCount = getUnscannedUnitCount(items || []);

  const renderItem = (item, index) => {
    if (!isFlatDisplayItem(item)) {
      return (
        <View
          key={`kit-${item.product_id || item.item_id}-${index}`}
          style={styles.kitItemWrapper}>
          <OrderKitItem
            item={item}
            index={index}
            listType={'unscanned'}
            redirectToItemDetail={onItemPress}
            shuffleTheItem={(sku) => onShuffle(sku, item.child_items?.[0]?.sku)}
            shuffleTheKitItem={(kitSku, childSku) => onShuffle(kitSku, childSku)}
          />
        </View>
      );
    }

    return (
      <View
        key={`single-${item.product_id || item.item_id}-${index}`}
        style={styles.singleItemWrapper}>
        <View style={styles.singleItemContainer}>
          <OrderItem
            item={item}
            testID={'redirectToItem'}
            redirectToItemDetail={onItemPress}
          />
          <OrderItemImage
            item={item}
            listType={'unscanned'}
            testID={'shuffleItem'}
            shuffleTheItem={(sku) => onShuffle(sku)}
          />
        </View>
      </View>
    );
  };

  const notFulfilledSection = checkIfAllFromNotFulfilled(items) ? null : (
    <NotFulfilledItems
      notFullFilledItems={notFulfilledItems}
      redirectToItemDetail={onItemPress}
      shuffleTheItem={sku => onShuffle(sku)}
    />
  );

  const headerElement = (
    <>
      {windowWidth > 900 && (
        <Text style={styles.headerTitle}>
          {totalUnScannedCount} Unscanned Items
        </Text>
      )}
      {sortAvailable && (
        <TouchableOpacity testID="boxchamp-sort-toggle" onPress={toggleSort} style={styles.sortToggle}>
          <Text testID={`boxchamp-sort-mode-${sortMode}`} style={styles.sortToggleText}>
            {sortMode === 'packing' ? 'Sort: Packing order' : 'Sort: Location'}
          </Text>
        </TouchableOpacity>
      )}
    </>
  );

  if (view) {
    return (
      <ScrollView style={styles.zonedContainer} showsVerticalScrollIndicator={false}>
        {headerElement}
        <BoxChampZonedList
          view={view}
          renderItem={renderItem}
          onOpenOptions={paused ? undefined : () => dispatch(openBoxChampOptions())}
        />
        {notFulfilledSection}
      </ScrollView>
    );
  }

  return (
    <ScrollableItemList
      items={displayItems}
      renderItem={renderItem}
      emptyText="No unscanned items"
      header={headerElement}
      footer={notFulfilledSection}
    />
  );
};

const styles = StyleSheet.create({
  headerTitle: {
    color: '#fff',
    fontSize: 15,
    fontFamily: 'Poppins_600SemiBold',
    fontWeight: 'bold',
    textAlign: 'center',
    backgroundColor: '#000',
    paddingVertical: 10
  },
  singleItemWrapper: {
    marginBottom: 8
  },
  singleItemContainer: {
    flexDirection: 'row',
    borderRadius: 30
  },
  kitItemWrapper: {
    marginBottom: 4
  },
  zonedContainer: {
    flex: 1,
    backgroundColor: 'transparent'
  },
  sortToggle: {
    alignSelf: 'flex-start',
    marginLeft: 8,
    marginVertical: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    backgroundColor: 'rgba(0,0,0,0.35)',
    borderRadius: 12
  },
  sortToggleText: {
    color: '#ffffff',
    fontSize: 13,
    fontFamily: 'Poppins_600SemiBold'
  }
});

export default UnScannedItemsList;
